import Question from "./Question";
import HTMLForm from "./HTML";
import FormLoader from "../Loaders/components/Form";
import useSubmissionInitializer from "../hooks/useSubmissionInitializer";
import { Fragment, useEffect, useMemo, useRef } from "react";
import Alert from "./Alert";
import { findFirstFocusableElement } from "../util/functions";
import useForm from "../hooks/useForm/index.js";
import { useDispatch, useSelector } from "react-redux";
import { usePrevious } from "@osu/react-ui";
import { clearAlert, selectIsAlertVisible } from "../../services/alert/slices.js";
import { useParams } from "react-router-dom";
import useGroupValidation from "../hooks/useGroupValidation";
import QuestionGroup from "./QuestionGroup";
import { selectByQuestionId } from "../../services/storage/slices";

function Submission() {
  const {
    isLoading: showLoader,
    identifiers = [],
    readOnly
  } = useSubmissionInitializer();
  const { type, id} = useParams();
  const showingAlert = useSelector(selectIsAlertVisible)
  const previousType = usePrevious(type);
  const previousID = usePrevious(id);

  const dispatch = useDispatch()

  const {
    ref: formRef,
    onDelete,
    onSave,
    onSubmit: _onSubmit,
    loading,
    showErrorForMissingField,
    onSubmitClick: _onSubmitClick,
  } = useForm();

  const {
    groupErrors,
    validateGroups,
    firstErrorRef,
  } = useGroupValidation(formRef);

  const onSubmit = (event) => {
    const groupsValid = validateGroups();
    if (!groupsValid) {
      event.preventDefault();
      return;
    }
    _onSubmit(event);
  };

  const onSubmitClick = () => {
    validateGroups();
    _onSubmitClick();
  };

  const alertRef = useRef()

  useEffect(() => {
    const typeChanged = previousType && previousType !== type;
    const idChanged = previousID && (previousID !== id);
    if (showingAlert && (typeChanged || idChanged)) {
      dispatch(clearAlert());
    }
  }, [dispatch, id, previousID, previousType, showingAlert, type])

  useEffect(() => {
    return () => {
      if (showingAlert) {
        dispatch(clearAlert());
      }
    }
  }, [dispatch, showingAlert])

  const onAlertClose = () => {
    const elToFocus = findFirstFocusableElement(formRef)
    if(elToFocus) {
      elToFocus.focus()
    }
  };

  // Build question groups from identifiers - only extract groupId
  const questionGroupIds = useSelector((state) => {
    return identifiers.map((qId) => {
      const q = selectByQuestionId(state, {
        identifier: id,
        type,
        questionId: qId
      });
      return q?.groupId || null;
    });
  }, (a, b) => a.length === b.length && a.every((val, i) => val === b[i]));

  const questionElements = useMemo(() => {
    const elements = [];
    let currentGroupId = null;
    let currentGroupQuestions = [];

    const flushGroup = () => {
      if (currentGroupId && currentGroupQuestions.length) {
        elements.push(
          <QuestionGroup
            key={`group-${currentGroupId}`}
            groupId={currentGroupId}
            hasError={!!groupErrors[currentGroupId]}
            ref={groupErrors[currentGroupId] ? firstErrorRef : undefined}
          >
            {currentGroupQuestions.map(({ qId, idx }) => (
              <Question
                key={`${qId}-${idx}`}
                id={qId}
                readOnly={readOnly}
                showErrorForMissingField={showErrorForMissingField}
              />
            ))}
          </QuestionGroup>
        );
      }
      currentGroupQuestions = [];
    };

    identifiers.forEach((qId, idx) => {
      const groupId = questionGroupIds[idx];

      if (groupId) {
        if (groupId !== currentGroupId) {
          flushGroup();
          currentGroupId = groupId;
        }
        currentGroupQuestions.push({ qId, idx });
      } else {
        flushGroup();
        currentGroupId = null;
        elements.push(
          <Question
            key={`${qId}-${idx}`}
            id={qId}
            readOnly={readOnly}
            showErrorForMissingField={showErrorForMissingField}
          />
        );
      }
    });
    flushGroup();

    return elements;
  }, [identifiers, questionGroupIds, readOnly, showErrorForMissingField, groupErrors, firstErrorRef]);

  return (
    <Fragment>
      <Alert ref={alertRef} onClose={onAlertClose} />
      {showLoader && <FormLoader />}
      {!showLoader && (
        <HTMLForm
          ref={formRef}
          readOnly={readOnly}
          loading={loading}
          onDelete={onDelete}
          onSave={onSave}
          onSubmit={onSubmit}
          onSubmitClick={onSubmitClick}
        >
          {questionElements}
        </HTMLForm>
      )}
    </Fragment>
  );
}

export default Submission;
